import { useCallback, useState } from "react";

const STORAGE_KEY = "qalimo.rentabilite.dismissedAlerts";

function storageKey(code: string, period: string) {
  return `${code}:${period}`;
}

function readDismissed(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.filter((v) => typeof v === "string")
      : [];
  } catch {
    return [];
  }
}

function writeDismissed(keys: string[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(keys));
  } catch {
    // Stockage indisponible (navigation privée, quota) : l'état reste en mémoire
    // et les alertes réapparaîtront au prochain chargement.
  }
}

/**
 * Alertes de l'onglet Rentabilité fermées par l'utilisateur, mémorisées dans le
 * navigateur par code d'alerte et par période affichée (année ou « Tous ») : une
 * alerte fermée sur 2026 réapparaît sur 2025.
 */
export default function useDismissedAlerts(period: string) {
  const [dismissed, setDismissed] = useState<string[]>(readDismissed);

  const isDismissed = useCallback(
    (code: string) => dismissed.includes(storageKey(code, period)),
    [dismissed, period],
  );

  const dismiss = useCallback(
    (code: string) => {
      setDismissed((current) => {
        const key = storageKey(code, period);
        if (current.includes(key)) return current;
        const next = [...current, key];
        writeDismissed(next);
        return next;
      });
    },
    [period],
  );

  /** Réaffiche toutes les alertes de la période courante. */
  const restore = useCallback(() => {
    setDismissed((current) => {
      const next = current.filter((key) => !key.endsWith(`:${period}`));
      if (next.length === current.length) return current;
      writeDismissed(next);
      return next;
    });
  }, [period]);

  return { isDismissed, dismiss, restore };
}
